import {type FC, type MouseEvent, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import type {NzbHealthDebugData, RecentNzbChecksData} from '@repo/shared/api/website_api';
import {errorAsString} from '@repo/shared/lib/type_utils';

import {Button} from '@repo/shared-web/components/core/button';
import {Input} from '@repo/shared-web/components/core/input';
import {useStringQueryString} from '@repo/shared-web/lib/use_query_string';

import {apiCall} from '@repo/website-frontend/lib/api';
import {formatReleaseDate} from '@repo/website-frontend/lib/date';

const PAGE_SIZE = 50;

export const NzbHealthDebugPage: FC = () => {
  const [requestedGuid, setRequestedGuid, requestedGuidRef] = useStringQueryString('guid', '');
  const [searchGuid, setSearchGuid] = useState(requestedGuid);
  const [refreshKey, setRefreshKey] = useState(0);
  const [recentChecks, setRecentChecks] = useState<RecentNzbChecksData | undefined>();
  const [releaseDetails, setReleaseDetails] = useState<NzbHealthDebugData | undefined>();
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [loadingRelease, setLoadingRelease] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setSearchGuid(requestedGuid);
  }, [requestedGuid]);

  useEffect(() => {
    let active = true;
    setLoadingRecent(true);
    setError(undefined);
    apiCall('POST /list-recent-nzb-checks', {paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setRecentChecks(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRecent(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  useEffect(() => {
    const guid = requestedGuid.trim();
    if (!guid) {
      setReleaseDetails(undefined);
      setLoadingRelease(false);
      return;
    }

    let active = true;
    setLoadingRelease(true);
    setReleaseDetails(undefined);
    setError(undefined);
    apiCall('POST /get-nzb-health-debug', {guid, paginationToken: undefined, limit: PAGE_SIZE})
      .then(result => {
        if (active) {
          setReleaseDetails(result);
        }
      })
      .catch(err => {
        if (active) {
          setError(errorAsString(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingRelease(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refreshKey, requestedGuid]);

  const handleRefreshClick = useCallback(() => {
    setRefreshKey(current => current + 1);
  }, []);

  const handleInspectClick = useCallback(() => {
    setRequestedGuid(searchGuid.trim());
    setRefreshKey(current => current + 1);
  }, [searchGuid, setRequestedGuid]);

  const handleInspectRowClick = useCallback(
    (evt: MouseEvent<HTMLElement>) => {
      const guid = evt.currentTarget.getAttribute('data-guid');
      if (!guid) {
        return;
      }
      setSearchGuid(guid);
      setRequestedGuid(guid);
      setRefreshKey(current => current + 1);
    },
    [setRequestedGuid],
  );

  const handleCloseReleaseClick = useCallback(() => {
    setRequestedGuid('');
    setReleaseDetails(undefined);
  }, [setRequestedGuid]);

  const handleLoadMoreClick = useCallback(async () => {
    if (!recentChecks?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /list-recent-nzb-checks', {
        limit: PAGE_SIZE,
        paginationToken: recentChecks.paginationToken,
      });
      setRecentChecks(current =>
        current
          ? {
              items: [...current.items, ...nextPage.items],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      setError(errorAsString(err));
    } finally {
      setLoadingMore(false);
    }
  }, [recentChecks?.paginationToken]);

  const handleLoadOlderReleaseChecksClick = useCallback(async () => {
    if (!requestedGuid || !releaseDetails?.paginationToken) {
      return;
    }
    setLoadingMore(true);
    setError(undefined);
    try {
      const nextPage = await apiCall('POST /get-nzb-health-debug', {
        guid: requestedGuid,
        limit: PAGE_SIZE,
        paginationToken: releaseDetails.paginationToken,
      });
      setReleaseDetails(current =>
        requestedGuidRef.current === requestedGuid && current?.guid === requestedGuid
          ? {
              ...current,
              checks: [...current.checks, ...nextPage.checks],
              paginationToken: nextPage.paginationToken,
            }
          : current,
      );
    } catch (err: unknown) {
      if (requestedGuidRef.current === requestedGuid) {
        setError(errorAsString(err));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [releaseDetails?.paginationToken, requestedGuid, requestedGuidRef]);

  const handleRunCheckClick = useCallback(async () => {
    await apiCall('POST /check-nzb', {id: requestedGuid});
    setRefreshKey(current => current + 1);
  }, [requestedGuid]);

  return (
    <Wrapper>
      <PageHeader>
        <div>
          <h1>NZB checks</h1>
          <p>See the latest availability checks across all releases.</p>
        </div>
        <Button onClick={handleRefreshClick} disabled={loadingRecent}>
          Refresh
        </Button>
      </PageHeader>

      {error ? <ErrorMessage>{error}</ErrorMessage> : <></>}

      <Panel>
        <SectionHeader>
          <div>
            <h2>Latest checks</h2>
            <p>One recent result per release, newest first.</p>
          </div>
          <Updated>Each check tests up to 10 file parts; a result applies to that sample.</Updated>
        </SectionHeader>

        {loadingRecent ? (
          <Message>Loading recent checks…</Message>
        ) : recentChecks?.items.length ? (
          <TableScroll>
            <ChecksTable>
              <thead>
                <tr>
                  <th>Checked</th>
                  <th>Release</th>
                  <th>Result</th>
                  <th>Found</th>
                  <th>Missing</th>
                  <th>Sampled</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentChecks.items.map(check => (
                  <tr key={check.id}>
                    <td>{formatTimestamp(check.ts)}</td>
                    <td>
                      <ReleaseTitle>{check.title ?? 'Release title unavailable'}</ReleaseTitle>
                      <Guid>{check.nzbId}</Guid>
                    </td>
                    <td>
                      <ResultBadge
                        $state={
                          check.checked === 0 ? 'empty' : check.failure > 0 ? 'missing' : 'found'
                        }
                      >
                        {check.checked === 0
                          ? 'Nothing tested'
                          : check.failure === 0
                            ? 'Sample passed'
                            : 'Missing parts'}
                      </ResultBadge>
                    </td>
                    <td>{check.success}</td>
                    <td>{check.failure}</td>
                    <td>{`${check.checked} of ${check.total}`}</td>
                    <td>
                      <Button data-guid={check.nzbId} onClick={handleInspectRowClick}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ChecksTable>
          </TableScroll>
        ) : (
          <Message>
            No checks yet. Open a release on NZB.su and choose “Check” to add the first result.
          </Message>
        )}

        {recentChecks?.paginationToken ? (
          <LoadMore onClickAsync={handleLoadMoreClick} loading={loadingMore}>
            Load older checks
          </LoadMore>
        ) : (
          <></>
        )}
      </Panel>

      <Panel>
        <SectionHeader>
          <div>
            <h2>Find a release</h2>
            <p>Enter its ID to see the check history or run another check.</p>
          </div>
        </SectionHeader>
        <LookupRow>
          <Input<string>
            aria-label="NZB release ID"
            placeholder="Paste an NZB release ID"
            value={searchGuid}
            syncState={setSearchGuid}
          />
          <Button onClick={handleInspectClick} disabled={searchGuid.trim().length === 0}>
            View release
          </Button>
        </LookupRow>
      </Panel>

      {loadingRelease ? <Message>Loading release…</Message> : <></>}
      {releaseDetails ? (
        <Panel>
          <SectionHeader>
            <div>
              <h2>{releaseDetails.release?.title ?? 'Release details'}</h2>
              <Guid>{releaseDetails.guid}</Guid>
              {releaseDetails.release ? (
                <ReleaseDate>Posted {formatReleaseDate(releaseDetails.release.pubTs)}</ReleaseDate>
              ) : (
                <></>
              )}
            </div>
            <ActionGroup>
              <Button onClickAsync={handleRunCheckClick}>Check now</Button>
              <Button onClick={handleCloseReleaseClick}>Close</Button>
            </ActionGroup>
          </SectionHeader>

          <PreviousChecksHeading>Check history</PreviousChecksHeading>
          {releaseDetails.checks.length ? (
            <TableScroll>
              <ChecksTable>
                <thead>
                  <tr>
                    <th>Checked</th>
                    <th>Result</th>
                    <th>Found</th>
                    <th>Missing</th>
                    <th>Sampled</th>
                  </tr>
                </thead>
                <tbody>
                  {releaseDetails.checks.map(check => (
                    <tr key={check.id}>
                      <td>{formatTimestamp(check.ts)}</td>
                      <td>
                        {check.checked === 0
                          ? 'Nothing tested'
                          : check.failure === 0
                            ? 'Sample passed'
                            : 'Missing parts'}
                      </td>
                      <td>{check.success}</td>
                      <td>{check.failure}</td>
                      <td>{`${check.checked} of ${check.total}`}</td>
                    </tr>
                  ))}
                </tbody>
              </ChecksTable>
            </TableScroll>
          ) : (
            <Message>No saved checks for this release.</Message>
          )}

          {releaseDetails.paginationToken ? (
            <LoadMore onClickAsync={handleLoadOlderReleaseChecksClick} loading={loadingMore}>
              Load older checks
            </LoadMore>
          ) : (
            <></>
          )}
        </Panel>
      ) : (
        <></>
      )}
    </Wrapper>
  );
};

NzbHealthDebugPage.displayName = 'NzbHealthDebugPage';

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  max-width: 1400px;
  width: 100%;
  align-self: center;
`;

const PageHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  h1 {
    margin: 0 0 8px;
  }
  p {
    margin: 0;
    color: #ffffffaa;
  }
`;

const Panel = styled.section`
  background: #1f242f;
  border: 1px solid #ffffff1a;
  border-radius: 8px;
  padding: 16px;
  h2 {
    margin: 0 0 6px;
    font-size: 18px;
  }
  p {
    margin: 0;
    color: #ffffffbb;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const Updated = styled.span`
  color: #ffffff88;
  font-size: 13px;
`;

const LookupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  > input {
    flex: 1;
    min-width: 0;
  }
  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const ChecksTable = styled.table`
  border-collapse: collapse;
  min-width: 760px;
  width: 100%;
  text-align: left;
  th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
  }
  th,
  td {
    border-bottom: 1px solid #ffffff12;
    padding: 11px 10px;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
    white-space: nowrap;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
    text-align: right;
  }
`;

const ReleaseTitle = styled.div`
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Guid = styled.div`
  color: #ffffff77;
  font-family: monospace;
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

const ResultBadge = styled.span<{$state: 'empty' | 'missing' | 'found'}>`
  color: ${p =>
    p.$state === 'missing' ? '#ffcb8a' : p.$state === 'found' ? '#9ee6ae' : '#ffffff88'};
  white-space: nowrap;
`;

const ReleaseDate = styled.p`
  margin-top: 6px !important;
`;

const PreviousChecksHeading = styled.h3`
  font-size: 15px;
  margin: 0 0 4px;
`;

const LoadMore = styled(Button)`
  margin-top: 12px;
`;

const Message = styled.div`
  color: #ffffff88;
  padding: 8px 0;
`;

const ErrorMessage = styled(Message)`
  color: #ff8585;
`;
